// Standalone Filter Component Types
// This file contains all the types needed for the standalone filter component

export enum FilterLogic {
  AND = 'AND',
  OR = 'OR',
}

export enum FilterOperation {
  IS = 'is',
  IS_NOT = 'is_not',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',
  CONTAINS = 'contains',
  DOES_NOT_CONTAIN = 'does_not_contain',
  EQUAL = 'equal',
  NOT_EQUAL = 'is_not_equal',
  GREATER_THAN = 'is_greater_than',
  GREATER_THAN_OR_EQUAL = 'is_greater_than_or_equal',
  LESS_THAN_OR_EQUAL = 'is_less_than_or_equal',
  LESS_THAN = 'is_less_than',
  BETWEEN = 'is_between',
  IS_WITHIN = 'is_within',
  IS_BEFORE = 'is_before',
  IS_AFTER = 'is_after',
  IS_ON_OR_BEFORE = 'is_on_or_before',
  IS_ON_OR_AFTER = 'is_on_or_after',
  START_WITH = 'starts_with',
  IN = 'in',
  NOT_IN = 'not_in',
  IS_ANY = 'is any',
  IS_NOT_ANY = 'is not any',
  HAS = 'has',
  DOES_NOT_HAVE = 'does_not_have',
}

export interface FilterField {
  id: string;
  key: string;
  label: string;
  operations: FilterOperation[];
}

export interface RangeValue {
  min?: number;
  max?: number;
}

export interface FilterCondition {
  id: string;
  field: FilterField | null;
  operation: FilterOperation | null;
  value: string | number | (string | number)[] | RangeValue | undefined;
}

export interface FilterRow {
  id: string;
  logic: FilterLogic;
  conditions: FilterCondition[];
}

export interface FilterBuilderProps {
  fields: FilterField[];
  filterRows: FilterRow[];
  onFilterRowsChange: (filterRows: FilterRow[]) => void;
  maxConditions?: number;
  className?: string;
  valueOptions?: { key: string; label: string }[];
  disabled?: boolean;
  hasError?: boolean;
}

export interface FilterRowProps {
  row: FilterRow;
  fields: FilterField[];
  onRowChange: (row: FilterRow) => void;
  onAddCondition: () => void;
  onRemoveCondition: (conditionIndex: number) => void;
  canAddCondition: boolean;
  maxConditionsReached: boolean;
  valueOptions?: { key: string; label: string }[];
  allSelectedFields?: string[]; // Array of already selected field keys to prevent duplicates
  disabled?: boolean;
}

export interface FilterConditionProps {
  condition: FilterCondition;
  conditionIndex: number;
  fields: FilterField[];
  onConditionChange: (condition: FilterCondition) => void;
  onRemoveCondition: () => void;
  showLogic: boolean;
  logic: FilterLogic;
  onLogicChange: (logic: FilterLogic) => void;
  valueOptions?: { key: string; label: string }[];
  allSelectedFields?: string[]; // Array of already selected field keys to prevent duplicates
  disabled?: boolean;
}

// Operations mapping for display
export const OPERATIONS_MAPPING: Record<FilterOperation, string> = {
  [FilterOperation.IS]: 'Is',
  [FilterOperation.IS_NOT]: 'Is not',
  [FilterOperation.IS_EMPTY]: 'Is empty',
  [FilterOperation.IS_NOT_EMPTY]: 'Is not empty',
  [FilterOperation.CONTAINS]: 'Contains',
  [FilterOperation.DOES_NOT_CONTAIN]: 'Does not contain',
  [FilterOperation.EQUAL]: 'Equals',
  [FilterOperation.NOT_EQUAL]: 'Does not equal',
  [FilterOperation.GREATER_THAN]: 'Greater than',
  [FilterOperation.GREATER_THAN_OR_EQUAL]: 'Greater than or equal',
  [FilterOperation.LESS_THAN_OR_EQUAL]: 'Less than or equal',
  [FilterOperation.LESS_THAN]: 'Less than',
  [FilterOperation.BETWEEN]: 'Between',
  [FilterOperation.IS_WITHIN]: 'Is within',
  [FilterOperation.IS_BEFORE]: 'Is before',
  [FilterOperation.IS_AFTER]: 'Is after',
  [FilterOperation.IS_ON_OR_BEFORE]: 'Is on or before',
  [FilterOperation.IS_ON_OR_AFTER]: 'Is on or after',
  [FilterOperation.START_WITH]: 'Starts with',
  [FilterOperation.IN]: 'In',
  [FilterOperation.NOT_IN]: 'Not in',
  [FilterOperation.IS_ANY]: 'Is any',
  [FilterOperation.IS_NOT_ANY]: 'Is not any',
  [FilterOperation.HAS]: 'Has',
  [FilterOperation.DOES_NOT_HAVE]: 'Does not have',
};
